import React from 'react';
import { useLanguage } from '../../context/LanguageContext';

const FiveWhys = ({ 
  selectedEightD,
  whyTrees,
  showForm,
  setShowForm,
  editingWhyTree,
  whyTreeForm,
  onFormChange,
  onNodeChange,
  onAddNode,
  onRemoveNode,
  onSave,
  onDelete,
  onEdit,
  onReset
}) => {
  const { t } = useLanguage();

  if (!selectedEightD) return null;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-orange-100">
      <h2 className="text-xl font-bold text-gray-800 mb-4">
        D4 - {t('acciones.fiveWhys.title')}
        <span className="ml-3 text-sm font-normal text-orange-600">
          ({selectedEightD.code})
        </span>
      </h2>

      {/* Botón para mostrar formulario */}
      {!showForm && !editingWhyTree && (
        <div className="mb-4">
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700"
          >
            + {t('acciones.fiveWhys.add')}
          </button>
        </div>
      )}

      {/* Formulario de 5 Whys */}
      {(showForm || editingWhyTree) && (
        <div className="bg-orange-50 p-4 rounded-lg mb-4">
          <h3 className="font-semibold text-gray-800 mb-3">
            {editingWhyTree ? t('acciones.fiveWhys.edit') : t('acciones.fiveWhys.add')}
          </h3>
        
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                {t('acciones.fiveWhys.category')}
              </label>
              <input
                type="text"
                name="method"
                value={whyTreeForm.method}
                onChange={onFormChange}
                placeholder="5whys, Ishikawa, etc."
                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="validated"
                  checked={whyTreeForm.validated}
                  onChange={onFormChange}
                  className="mr-2 w-4 h-4"
                />
                <span className="text-sm font-semibold text-gray-800">
                  {t('acciones.fiveWhys.validated')}
                </span>
              </label>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              {t('acciones.fiveWhys.rootStatement')}
            </label>
            <textarea
              name="problem_statement"
              value={whyTreeForm.problem_statement}
              onChange={onFormChange}
              rows="2"
              placeholder={t('acciones.fiveWhys.rootStatementPlaceholder')}
              className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900"
            />
          </div>

          {/* Nodos de Why */}
          <div className="space-y-3 mb-4">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold text-gray-800">
                {t('acciones.fiveWhys.whyLevels')}
              </h4>
              <button
                onClick={onAddNode}
                disabled={whyTreeForm.nodes.length >= 5}
                className="px-3 py-1 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:bg-gray-400"
              >
                + {t('acciones.fiveWhys.addLevel')}
              </button>
            </div>

            {whyTreeForm.nodes.map((node, index) => (
              <div key={index} className="bg-white p-3 rounded border border-orange-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-orange-600">
                    {t('acciones.fiveWhys.level')} {node.depth + 1}
                  </span>
                  {whyTreeForm.nodes.length > 1 && (
                    <button
                      onClick={() => onRemoveNode(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-semibold"
                    >
                      ✕ {t('acciones.buttons.remove')}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {t('acciones.fiveWhys.question')}
                    </label>
                    <input
                      type="text"
                      value={node.why_text}
                      onChange={(e) => onNodeChange(index, 'why_text', e.target.value)}
                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm text-gray-900"
                      placeholder="¿Por qué ocurrió esto?"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {t('acciones.fiveWhys.answer')}
                    </label>
                    <input
                      type="text"
                      value={node.category || ''}
                      onChange={(e) => onNodeChange(index, 'category', e.target.value)}
                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm text-gray-900"
                      placeholder="Man, Machine, Method, etc."
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={onSave}
              className="px-4 py-2 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700"
            >
              {editingWhyTree ? t('acciones.buttons.update') : t('acciones.buttons.add')}
            </button>
            <button
              onClick={onReset}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              {t('acciones.buttons.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Lista de Why Trees */}
      <div className="space-y-4">
        {whyTrees.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            {t('acciones.fiveWhys.noTrees')}
          </p>
        ) : (
          whyTrees.map(tree => (
            <div key={tree.id} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <span className="font-semibold text-orange-600">
                    {t('acciones.fiveWhys.category')}: {tree.method}
                  </span>
                  {tree.validated && (
                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                      ✓ {t('acciones.fiveWhys.validated')}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {

                      onEdit(tree);
                    }}
                    className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded-lg text-sm font-semibold hover:bg-blue-100"
                  >
                    {t('acciones.buttons.edit')}
                  </button>
                  <button
                    onClick={() => onDelete(tree.id)}
                    className="px-3 py-1 bg-red-50 text-red-700 border border-red-300 rounded-lg text-sm font-semibold hover:bg-red-100"
                  >
                    {t('acciones.buttons.delete')}
                  </button>
                </div>
              </div>
              <div className="mb-3">
                <p className="text-sm text-gray-700">
                  <strong>{t('acciones.fiveWhys.rootStatement')}:</strong> {tree.problem_statement}
                </p>
              </div>
              <div className="space-y-2">
                {tree.nodes && tree.nodes.map((node, idx) => (
                  <div key={node.id || idx} className="bg-white p-2 rounded border border-gray-200">
                    <div className="text-xs font-semibold text-orange-600 mb-1">
                      {t('acciones.fiveWhys.level')} {(node.depth || 0) + 1}
                    </div>
                    <div className="text-sm">
                      <span className="font-semibold">{node.why_text}</span>
                      {node.category && (
                        <>
                          <br />
                          <span className="text-gray-700">→ {node.category}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FiveWhys;
